// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Dictation for the referee's question box, on web only.
 *
 * Uses the browser's own SpeechRecognition — no dependency, no key, no vendor. Native gets a
 * separate on-device implementation later; until then `supported` is false there and the caller
 * hides the button rather than showing one that does nothing.
 *
 * The transcript is handed to the caller as an editable draft, never sent straight to the referee:
 * "Stableford", "Nassau" and "pliktområde" are exactly the words recognition mangles, and a wrong
 * transcript should cost a keystroke rather than a question off the daily allowance.
 */

/** The bits of the Web Speech API we use. TypeScript ships no lib for it. */
type RecognitionAlternative = { transcript: string };
type RecognitionResult = { readonly length: number; isFinal: boolean; 0: RecognitionAlternative };
type RecognitionResultList = { readonly length: number; [index: number]: RecognitionResult };
type RecognitionEvent = { resultIndex: number; results: RecognitionResultList };
type RecognitionErrorEvent = { error: string };

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
};

type RecognitionConstructor = new () => Recognition;

/**
 * BCP-47 tag for the recognizer. The app's locale is a bare language code, and the Web Speech API
 * wants a region — without one Chrome falls back to the browser UI language, which is how a
 * Swedish player ends up dictating into an English recognizer.
 */
export function toRecognitionLocale(locale: string | undefined): string {
  const language = (locale ?? '').trim().toLowerCase().split(/[-_]/)[0];
  switch (language) {
    case 'sv':
      return 'sv-SE';
    case 'da':
      return 'da-DK';
    default:
      return 'en-GB';
  }
}

function getRecognitionConstructor(): RecognitionConstructor | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const candidate = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null;
}

type Options = {
  locale?: string;
  /** Called with the full dictated text as it firms up; the caller puts it in the draft. */
  onTranscript: (text: string) => void;
};

type SpeechInput = {
  /** False on native and in browsers without the API — the caller hides the button. */
  supported: boolean;
  listening: boolean;
  /** An i18n key suffix under `referee.errors`, or null. */
  errorCode: string | null;
  toggle: () => void;
};

export function useSpeechInput({ locale, onTranscript }: Options): SpeechInput {
  const Constructor = useMemo(getRecognitionConstructor, []);
  const [listening, setListening] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const recognitionRef = useRef<Recognition | null>(null);

  // Kept in a ref so restarting recognition doesn't depend on the caller memoising its callback.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  // A live recognizer holds the microphone open; leaving the screen must release it.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!Constructor) return;
    setErrorCode(null);

    const recognition = new Constructor();
    recognition.lang = toRecognitionLocale(locale);
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      // Rebuild the whole utterance each time: interim results are revised in place, so appending
      // deltas would stutter words back into the draft.
      let text = '';
      for (let index = 0; index < event.results.length; index += 1) {
        text += event.results[index][0].transcript;
      }
      onTranscriptRef.current(text.trim());
    };

    recognition.onerror = (event) => {
      // "aborted" is what we cause ourselves by navigating away — not worth showing.
      if (event.error !== 'aborted') {
        setErrorCode(event.error === 'not-allowed' ? 'micDenied' : 'micFailed');
      }
      recognitionRef.current = null;
      setListening(false);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch {
      // Chrome throws if start() is called while a previous session is still winding down.
      recognitionRef.current = null;
      setListening(false);
      setErrorCode('micFailed');
    }
  }, [Constructor, locale]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
      return;
    }
    start();
  }, [listening, start, stop]);

  return { supported: Constructor !== null, listening, errorCode, toggle };
}
